import { useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router'
import { MdOutlineAssignment } from 'react-icons/md'
import { ResourceList } from '@/Core/UI/CrudResource'
import { createCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { applyInitialFilter } from '@/Core/UI/CrudResource/Helpers/applyInitialFilter'
import { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import { AssignmentResource } from '../Resources/AssignmentResource'
import { MultiAssignmentDrawer } from '../Components/MultiAssignmentDrawer'
import { MultiAssignmentDrawerStore } from '../Stores/MultiAssignmentDrawerStore'
import { assignmentFilterFromQuery, stripDashboardQuery } from '../Helpers/filterFromQuery'

/**
 * Standalone tasks created manually by a dispatcher: executor IS NULL.
 * This is the only page where the "Add" (create) button is available.
 */
const AssignmentsWithoutRequestsPage = observer(() => {
    const { search } = useLocation()
    // Початковий статус-фільтр з query (перехід з головної: ?status=...).
    const filterInitialValues = useMemo(() => assignmentFilterFromQuery(search), [search])

    const [multiStore] = useState(() => new MultiAssignmentDrawerStore())

    const listContext = useMemo(() => {
        // Standalone tasks have no linked transport request, so the request-derived
        // columns ('ID заявки', 'Номер заявки в ERP') would always render '—' here.
        // Drop them only on this page; the "with requests" page keeps them.
        // Вантаж standalone-задачі живе на самій задачі, а не на потребі заявки,
        // тож колонка читає власне поле (базовий рендер іде через vehicleNeed).
        const columns = (AssignmentResource.table?.columns ?? [])
            .filter((col: any) => col.key !== 'requestId' && col.key !== 'requestErp')
            .map((col: any) =>
                col.key === 'cargo' ? { ...col, render: (_: any, row: any) => row.cargo?.title ?? '—' } : col
            )

        // З тієї ж причини фільтр по вантажу має бити у власне поле задачі:
        // filterCargo шукає по executor.vehicleNeed.cargo, який тут завжди null.
        const filter = {
            ...AssignmentResource.filter,
            includeFields: ((AssignmentResource.filter?.['includeFields'] as string[]) ?? []).map((f: string) =>
                f === 'filterCargo' ? 'filterCargoOwn' : f
            ),
        }

        const ctx = createCrudResourceContext({
            contextType: 'list',
            registryKey: 'Assignment.WithoutRequests.list',
            resource: {
                ...AssignmentResource,
                key: 'Assignment.WithoutRequests',
                table: { ...AssignmentResource.table, columns },
                filter,
                // Заголовок картки списку — назва сторінки (перемикача над карткою більше
                // немає); resource.title лишається «Завдання» для заголовка картки запису
                headerPage: { ...AssignmentResource['headerPage'], title: 'Задачі без заявок' },
            },
            prefilter: { noRequest: true },
        } as any)
        // Статус з query — як користувацький фільтр (щоб «Очистити» його скидав).
        applyInitialFilter(ctx, filterInitialValues)
        stripDashboardQuery()
        // Дає доступ AssignmentResource.headerPage AddMultipleButton-handler-у
        // до drawer-стору (кнопка "Додати кілька" видима лише на цій сторінці).
        ;(ctx as any).setStorage?.('multiAssignmentStore', multiStore)
        return ctx
    }, [filterInitialValues, multiStore])

    return (
        <>
            <ResourceList crudContext={listContext} registryKey="Assignment.WithoutRequests.list" />
            <MultiAssignmentDrawer store={multiStore} onCreated={() => listContext?.actions?.reload?.()} />
        </>
    )
})

export const pageConfig: IPageConfig = {
    route: '/assignments/without-requests',
    key: 'Assignment.WithoutRequests',
    title: 'Задачі без заявок',
    icon: <MdOutlineAssignment />,
}

export default AssignmentsWithoutRequestsPage
